import { HttpClient, HttpParams } from '@angular/common/http';
import {
  FeatureConfigResponseDTO,
  MIMContributingTestsBFFRequestDTO,
  MIMContributingTestsBFFResponseDTO
} from '@libs/api';
import {
  IClientProcessInfraSettingsCreateRequest,
  IClientProcessInfraSettingsManagement,
  IClientProcessInfraSettingsTaskDefinitionValidation,
  IClientProcessInfraSettingsUpdateRequest,
  IClientProcessInfraSettingsUpdateStatusRequest,
  IClientProcessInfraSettingsWarehouse,
  ICredentialDetails,
  IFeatureConfigAssignRequest,
  IFeatureConfigManagement,
  IFeatureConfigUpdateStatusRequest,
  IFeatureDetails,
  IFeatureDetailsCreateRequest,
  IFeatureDetailsUpdateDescriptionRequest,
  IFeatureDetailsUpdateStatusRequest,
  IFlattenedBrand,
  IHealthMonitor,
  IUserPermissions
} from '@libs/apis';
import { lastValueFrom } from 'rxjs';
import { Utility } from '../helpers/utility';
import { ClientType } from '../models/ClientType';
import { IAddBrandRequest } from '../models/IAddBrandRequest';
import {
  IAirflowAgentRequest,
  IAirflowAgentResponse,
  IAirflowDagDataResponse
} from '@libs/apis';
import { IBrand } from '../models/IBrand';
import { IClient, INewClientRequestBody } from '../models/IClient';
import {
  ICommonLookups,
  ICommonLookupTypes,
  INewCommonLookups
} from '../models/ICommonLookups';
import { IChildNode, IGraphNode } from '../models/IComponentGraphNode';
import { ICredentials } from '../models/ICredentials';
import { ICurrencyConfig } from '../models/ICurrencyConfig';
import { IEventWatcherRule } from '../models/IEventWatcherRule';
import {
  IGeoTestCalibration,
  IOptInChangePayload
} from '../models/IGeoTestCalibration';
import { IPlatformApiOperation } from '../models/IPlatformApiOperation';
import { IUserAccess, User } from '../models/User';

const promiseFrom = lastValueFrom;

export class PlatformApis implements IPlatformApiOperation {
  private constructor(
    private readonly httpClient: HttpClient,
    private readonly apiV1: string,
    private readonly apiV2: string
  ) {}

  public static platformApiOperation(
    httpClient: HttpClient,
    apiV1: string,
    apiV2: string
  ): IPlatformApiOperation {
    return new PlatformApis(httpClient, apiV1, apiV2);
  }

  public async getUser(): Promise<User> {
    const url = Utility.buildURL({
      url: `${this.apiV2}/user`
    });
    return promiseFrom(this.httpClient.get<User>(url));
  }

  public async getAllRoles(): Promise<any> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/user-roles`
    });
    return promiseFrom(this.httpClient.get(url));
  }

  public async getAllApis(moduleSearchText: string): Promise<any> {
    const queryParams = new HttpParams({
      fromObject: {
        name: moduleSearchText
      }
    });
    return promiseFrom(
      this.httpClient.get(`${this.apiV1}/module-operations`, {
        params: queryParams
      })
    );
  }

  public async getModulePermissionsByRole(roleName: string): Promise<any> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/user-role-permissions/by-role/:roleName`,
      pathVariables: { roleName }
    });
    return promiseFrom(this.httpClient.get(url));
  }

  public async getUserAccess(userName: string): Promise<any> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/user-access/by-username/:userName`,
      pathVariables: { userName }
    });
    return promiseFrom(this.httpClient.get(url));
  }

  public async saveOrUpdateUserAccess(data: any): Promise<any> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/user-access/`
    });
    return promiseFrom(this.httpClient.put(url, data));
  }

  public async saveOrUpdateRolePermission(data: any): Promise<any> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/user-role-permissions/`
    });
    return promiseFrom(this.httpClient.put(url, data));
  }

  public async getClientById(clientId: number): Promise<IClient> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/clients/${clientId}`
    });
    return promiseFrom(this.httpClient.get<IClient>(url));
  }

  public async getAccessibleClients(): Promise<IUserPermissions> {
    const url = Utility.buildURL({
      url: `${this.apiV2}/user/permissions`
    });
    return promiseFrom(this.httpClient.get<IUserPermissions>(url));
  }

  public async getAllClients(): Promise<IClient[]> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/clients`
    });
    return promiseFrom(this.httpClient.get<IClient[]>(url));
  }

  public async getAllBrands(): Promise<IBrand[]> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/brands`
    });
    return promiseFrom(this.httpClient.get<IBrand[]>(url));
  }

  public async getAllFlattenedBrands(): Promise<IFlattenedBrand[]> {
    return this.getAccessibleClients().then((permission) => {
      localStorage.setItem('userInfo', JSON.stringify(permission));
      return Utility.flattenBrands(permission.clients);
    });
  }

  public async getClients(): Promise<IClient[]> {
    return this.getAccessibleClients().then((accessibleClients) =>
      accessibleClients.userType === 'external'
        ? accessibleClients.clients
        : this.getAllClients()
    );
  }

  public async createClient(
    clientType: ClientType,
    newClientRequestBody: INewClientRequestBody
  ): Promise<IClient> {
    const url = Utility.buildURL({
      url: `${this.apiV2}/clients/:clientType`,
      pathVariables: {
        clientType
      }
    });
    return promiseFrom(
      this.httpClient.post<IClient>(url, newClientRequestBody)
    );
  }

  public async updateClientDetails(
    clientId: number,
    data: IClient
  ): Promise<IClient> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/clients/:clientId`,
      pathVariables: { clientId }
    });
    return promiseFrom(this.httpClient.put<IClient>(url, data));
  }

  public async getBrands(clientId: number): Promise<IBrand[]> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/brands`,
      queryParameters: { clientId }
    });
    return promiseFrom(this.httpClient.get<IBrand[]>(url));
  }

  public async updateBrandDetails(
    brandId: number,
    data: IBrand
  ): Promise<IFlattenedBrand> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/brands/:brandId`,
      pathVariables: { brandId }
    });
    return promiseFrom(this.httpClient.put<IFlattenedBrand>(url, data));
  }

  public async addNewBrand(postData: IAddBrandRequest): Promise<IBrand> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/brands`
    });
    return promiseFrom(this.httpClient.post<IBrand>(url, postData));
  }

  public async getHealthMonitorData(
    clientId: number
  ): Promise<IHealthMonitor[]> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/health-monitor`,
      queryParameters: { clientId }
    });
    return promiseFrom(this.httpClient.get<IHealthMonitor[]>(url));
  }

  public async getCredentials(clientId: number): Promise<ICredentialDetails[]> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/credentials`,
      queryParameters: { clientId }
    });
    return promiseFrom(this.httpClient.get<ICredentialDetails[]>(url));
  }

  public async getCommonLookupTypes(
    status?: string,
    lookupType?: string
  ): Promise<ICommonLookups[]> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/common-lookup`,
      queryParameters: {
        status: status || null,
        lookupType: lookupType || null
      }
    });
    return promiseFrom(this.httpClient.get<ICommonLookups[]>(url));
  }

  public async getActiveCommonLookupTypes(
    lookupType: string
  ): Promise<ICommonLookupTypes[]> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/common-lookup/lookup-type`,
      queryParameters: {
        lookupType: lookupType || null
      }
    });
    return promiseFrom(this.httpClient.get<ICommonLookupTypes[]>(url));
  }

  public async createCommonLookup(
    commonLookups: INewCommonLookups[]
  ): Promise<ICommonLookups[]> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/common-lookup`
    });
    return promiseFrom(
      this.httpClient.post<ICommonLookups[]>(url, { commonLookups })
    );
  }

  public async updateCommonLookups(
    ids?: number[],
    commonLookups?: INewCommonLookups[]
  ): Promise<ICommonLookups[]> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/common-lookup`
    });
    const body = {
      ids: ids || null,
      commonLookups: commonLookups || null
    };
    return promiseFrom(this.httpClient.put<ICommonLookups[]>(url, body));
  }

  public async getCurrencyConfigs(): Promise<ICurrencyConfig[]> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/currency-config`
    });
    return promiseFrom(this.httpClient.get<ICurrencyConfig[]>(url));
  }

  public async getAlertSubscriptions(): Promise<IEventWatcherRule[]> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/event-watcher`
    });
    return promiseFrom(this.httpClient.get<IEventWatcherRule[]>(url));
  }

  public async getRuleById(id: number): Promise<IEventWatcherRule> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/event-watcher/:id`,
      pathVariables: { id }
    });
    return promiseFrom(this.httpClient.get<IEventWatcherRule>(url));
  }

  public async updateEventWatcherRule(rule: any): Promise<IEventWatcherRule> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/event-watcher`
    });
    return promiseFrom(this.httpClient.put<IEventWatcherRule>(url, rule));
  }

  public async createEventWatcherRule(rule: any): Promise<IEventWatcherRule> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/event-watcher`
    });
    return promiseFrom(this.httpClient.post<IEventWatcherRule>(url, rule));
  }

  public async getComponentGraphData(productId?: string): Promise<any> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/component-graph`,
      queryParameters: { productId: productId || null }
    });
    return promiseFrom(this.httpClient.get<any>(url));
  }

  public async getAllClientCredentials(
    clientId: number
  ): Promise<ICredentials[]> {
    const url = Utility.buildURL({
      url: `${this.apiV2}/credentials`,
      queryParameters: { clientId }
    });
    return promiseFrom(this.httpClient.get<ICredentials[]>(url));
  }

  public async getCredentialById(id: number): Promise<ICredentials> {
    const url = Utility.buildURL({
      url: `${this.apiV2}/credentials/:id`,
      pathVariables: { id }
    });
    return promiseFrom(this.httpClient.get<ICredentials>(url));
  }

  public async getDefaultClientCredentials(): Promise<ICredentials[]> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/credentials/default-client-credentials`
    });
    return promiseFrom(this.httpClient.get<ICredentials[]>(url));
  }

  public async createCredential(
    credentials: ICredentials
  ): Promise<ICredentials> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/credentials`
    });
    return promiseFrom(this.httpClient.post<ICredentials>(url, credentials));
  }

  public async updateCredential(
    id: number,
    credentials: ICredentials
  ): Promise<ICredentials> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/credentials/:id`,
      pathVariables: { id }
    });
    return promiseFrom(this.httpClient.put<ICredentials>(url, credentials));
  }

  public async getCredentialConnectionTypes(): Promise<
    Record<string, string>[]
  > {
    const url = Utility.buildURL({
      url: `${this.apiV1}/credentials/connection-types`
    });
    return promiseFrom(this.httpClient.get<Record<string, string>[]>(url));
  }

  public async getActiveCleanrooms(): Promise<Record<string, string>[]> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/clients/cleanroom`
    });
    return promiseFrom(this.httpClient.get<Record<string, string>[]>(url));
  }

  public async queryAirflowAgent(
    request: IAirflowAgentRequest
  ): Promise<IAirflowAgentResponse> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/airflow-agent/query`
    });
    return promiseFrom(
      this.httpClient.post<IAirflowAgentResponse>(url, request)
    );
  }

  public async addNodeToComponentGraphData(node: IGraphNode): Promise<any> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/component-graph/node`
    });
    return promiseFrom(this.httpClient.post<any>(url, node));
  }

  public async addChildToComponentGraphData(node: IChildNode): Promise<any> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/component-graph/child-node`
    });
    return promiseFrom(this.httpClient.post<any>(url, node));
  }

  public async getComponentsByType(componentType: string): Promise<any> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/component-graph/component`,
      queryParameters: { componentType }
    });
    return promiseFrom(this.httpClient.get<any>(url));
  }

  public async linkExistingComponent(
    parentComponentId: number,
    componentId: number
  ): Promise<any> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/component-graph/link-node`
    });
    return promiseFrom(
      this.httpClient.post<any>(url, { parentComponentId, componentId })
    );
  }

  public async getAllUserAccess(): Promise<IUserAccess[]> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/user-access`
    });
    return promiseFrom(this.httpClient.get<IUserAccess[]>(url));
  }

  public async getAwsCredentials(
    clientId: number,
    datasourceId: string
  ): Promise<ICredentials> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/credentials/send-aws-credentials`,
      queryParameters: { clientId, datasourceId }
    });
    return promiseFrom(this.httpClient.get<ICredentials>(url));
  }

  public async getAllStudyAndCellConfigDetails(clientId: number): Promise<any> {
    const url = Utility.buildURL({
      url: `${this.apiV2}/geo-test-calibration`,
      queryParameters: { clientId }
    });
    return promiseFrom(this.httpClient.get<any>(url));
  }

  public async getTestCalibrationStatus(
    mimContributingTestsBFFRequestDTO: MIMContributingTestsBFFRequestDTO
  ): Promise<MIMContributingTestsBFFResponseDTO> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/bff/mim-page/mim-contributing-tests`
    });
    return promiseFrom(
      this.httpClient.post<MIMContributingTestsBFFResponseDTO>(
        url,
        mimContributingTestsBFFRequestDTO
      )
    );
  }

  public async getTestCalibrationStatusForStudy(
    mimContributingTestsBFFRequestDTO: MIMContributingTestsBFFRequestDTO
  ): Promise<MIMContributingTestsBFFResponseDTO> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/bff/mim-page/mim-contributing-tests/status`
    });
    return promiseFrom(
      this.httpClient.post<MIMContributingTestsBFFResponseDTO>(
        url,
        mimContributingTestsBFFRequestDTO
      )
    );
  }

  public async updateOptInStatus(
    OptInChangePayload: IOptInChangePayload
  ): Promise<IGeoTestCalibration[]> {
    const url = Utility.buildURL({
      url: `${this.apiV2}/geo-test-calibration/opt-in-out`
    });
    return promiseFrom(this.httpClient.post<any>(url, OptInChangePayload));
  }

  public async getAllFeatureConfigs(
    clientId: number,
    brandId: number
  ): Promise<FeatureConfigResponseDTO[]> {
    const url = Utility.buildURL({
      url: `${this.apiV2}/feature-config`,
      queryParameters: {
        clientId,
        brandId
      }
    });
    return promiseFrom(this.httpClient.get<FeatureConfigResponseDTO[]>(url));
  }

  public async getFeatureConfigsForManagement(
    clientId: number,
    brandId?: number
  ): Promise<IFeatureConfigManagement[]> {
    const queryParameters: Record<string, number> = { clientId };
    if (brandId !== undefined && brandId !== null) {
      queryParameters['brandId'] = brandId;
    }

    const url = Utility.buildURL({
      url: `${this.apiV1}/feature-config/all`,
      queryParameters
    });
    return promiseFrom(this.httpClient.get<IFeatureConfigManagement[]>(url));
  }

  public async assignFeatureConfig(
    request: IFeatureConfigAssignRequest
  ): Promise<IFeatureConfigManagement> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/feature-config/assign`
    });
    return promiseFrom(
      this.httpClient.post<IFeatureConfigManagement>(url, request)
    );
  }

  public async getAllFeatureDetails(): Promise<IFeatureDetails[]> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/feature-details`
    });
    return promiseFrom(this.httpClient.get<IFeatureDetails[]>(url));
  }

  public async createFeatureDetails(
    request: IFeatureDetailsCreateRequest
  ): Promise<IFeatureDetails> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/feature-details`
    });
    return promiseFrom(this.httpClient.post<IFeatureDetails>(url, request));
  }

  public async updateFeatureDetailsStatus(
    request: IFeatureDetailsUpdateStatusRequest
  ): Promise<IFeatureDetails> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/feature-details/status`
    });
    return promiseFrom(this.httpClient.put<IFeatureDetails>(url, request));
  }

  public async updateFeatureDetailsDescription(
    request: IFeatureDetailsUpdateDescriptionRequest
  ): Promise<IFeatureDetails> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/feature-details/description`
    });
    return promiseFrom(this.httpClient.put<IFeatureDetails>(url, request));
  }

  public async updateFeatureConfigStatus(
    request: IFeatureConfigUpdateStatusRequest
  ): Promise<void> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/feature-config/status`
    });
    return promiseFrom(this.httpClient.put<void>(url, request));
  }

  public async getClientProcessInfraSettingsForManagement(
    clientId?: number
  ): Promise<IClientProcessInfraSettingsManagement[]> {
    const queryParameters: Record<string, number> = {};
    if (clientId !== undefined && clientId !== null) {
      queryParameters['clientId'] = clientId;
    }

    const url = Utility.buildURL({
      url: `${this.apiV1}/client-process-infra-settings/all`,
      queryParameters
    });
    return promiseFrom(
      this.httpClient.get<IClientProcessInfraSettingsManagement[]>(url)
    );
  }

  public async createClientProcessInfraSettings(
    request: IClientProcessInfraSettingsCreateRequest
  ): Promise<IClientProcessInfraSettingsManagement> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/client-process-infra-settings`
    });
    return promiseFrom(
      this.httpClient.post<IClientProcessInfraSettingsManagement>(url, request)
    );
  }

  public async updateClientProcessInfraSettings(
    request: IClientProcessInfraSettingsUpdateRequest
  ): Promise<IClientProcessInfraSettingsManagement> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/client-process-infra-settings`
    });
    return promiseFrom(
      this.httpClient.put<IClientProcessInfraSettingsManagement>(url, request)
    );
  }

  public async updateClientProcessInfraSettingsStatus(
    request: IClientProcessInfraSettingsUpdateStatusRequest
  ): Promise<IClientProcessInfraSettingsManagement> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/client-process-infra-settings/status`
    });
    return promiseFrom(
      this.httpClient.put<IClientProcessInfraSettingsManagement>(url, request)
    );
  }

  public async validateClientProcessInfraSettingsTaskDefinition(
    processType: string,
    processRunTimeSize: string
  ): Promise<IClientProcessInfraSettingsTaskDefinitionValidation> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/client-process-infra-settings/task-definition/validate`,
      queryParameters: {
        processType,
        processRunTimeSize
      }
    });
    return promiseFrom(
      this.httpClient.get<IClientProcessInfraSettingsTaskDefinitionValidation>(
        url
      )
    );
  }

  public async getClientProcessInfraSettingsWarehouses(): Promise<
    IClientProcessInfraSettingsWarehouse[]
  > {
    const url = Utility.buildURL({
      url: `${this.apiV1}/client-process-infra-settings/warehouses`
    });
    return promiseFrom(
      this.httpClient.get<IClientProcessInfraSettingsWarehouse[]>(url)
    );
  }

  public async getDagList(): Promise<any> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/airflow-orchestrator/dags`
    });
    return promiseFrom(this.httpClient.get<any>(url));
  }

  public async isDagRunning(dagId: string): Promise<{ isRunning: boolean }> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/airflow-orchestrator/is-running/:dagId`,
      pathVariables: { dagId }
    });
    return promiseFrom(this.httpClient.get<{ isRunning: boolean }>(url));
  }

  public async triggerDag(dagId: string): Promise<any> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/airflow-orchestrator/trigger/:dagId`,
      pathVariables: { dagId }
    });
    return promiseFrom(this.httpClient.post<any>(url, {}));
  }

  public async getDagDataByClientId(
    clientId: number
  ): Promise<IAirflowDagDataResponse> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/airflow-agent/dag/:clientId/details`,
      pathVariables: { clientId: clientId.toString() }
    });
    return promiseFrom(this.httpClient.get<IAirflowDagDataResponse>(url));
  }

  public async getShopNames(clientId: number): Promise<string[]> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/ds-instances/shopify/shop-names`,
      queryParameters: {
        clientId
      }
    });
    return promiseFrom(this.httpClient.get<string[]>(url));
  }
}
